import React, { useState, useMemo } from 'react';
import {
  Button,
  DisplayText,
  Popover,
  TextStyle
} from '@superops/ui-design-system';
import { SVGIcon } from '@superops/super-icons';
import { InlineMultiTextField, toast } from '../../../index';
import { validateForm } from '@nile/components/src/Validators';
import { emailValidator } from '@nile/components/src/Validators/fields';
import { FormattedMessage, useIntl } from 'react-intl';
import { getTranslatedText } from '@nile/components';

interface EmailSenderProps {
  onSendEmail: (emailAddresses: string[]) => Promise<boolean>;
  sendEmailLoading: boolean;
  disabled?: boolean;
}

// Use the existing validation system from the codebase
const validateEmail = (email: string): boolean => {
  const trimmedEmail = email.trim();
  if (!trimmedEmail) return false;

  const result = validateForm(
    { email: trimmedEmail },
    {
      email: emailValidator({ mandatory: true })
    }
  );
  return !result; // validateForm returns undefined for valid, object for invalid
};

const EmailSender: React.FC<EmailSenderProps> = ({
  onSendEmail,
  sendEmailLoading,
  disabled = false
}) => {
  const [emailAddresses, setEmailAddresses] = useState<string[]>([]);
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [preventClose, setPreventClose] = useState(false);
  const intl = useIntl();

  // Validate all email addresses
  const emailValidation = useMemo(() => {
    const validEmails: string[] = [];
    const invalidEmails: string[] = [];

    emailAddresses.forEach((email) => {
      if (validateEmail(email)) {
        validEmails.push(email);
      } else {
        invalidEmails.push(email);
      }
    });

    return {
      validEmails,
      invalidEmails,
      hasInvalidEmails: invalidEmails.length > 0,
      allValid: emailAddresses.length > 0 && invalidEmails.length === 0
    };
  }, [emailAddresses]);

  const handleSendEmail = async () => {
    // Only send if all emails are valid
    if (!emailValidation.allValid) {
      return;
    }

    const success = await onSendEmail(emailValidation.validEmails);
    if (success) {
      toast(
        'success',
        getTranslatedText(intl, 'mdm.settings.enrolment.email.sentTo.requester')
      );
      setPopoverOpen(false);
      setEmailAddresses([]);
      setPreventClose(false);
    }
  };

  const handleCancel = () => {
    setPopoverOpen(false);
    setEmailAddresses([]);
    setPreventClose(false);
  };

  const handlePopoverClose = () => {
    if (!preventClose) {
      setPopoverOpen(false);
    }
  };

  const handleItemAdded = (item: any, allItems: string[]) => {
    setEmailAddresses(allItems);
  };

  const handleItemDeleted = (item: any, allItems: string[]) => {
    // Temporarily prevent popover from closing when deleting items
    setPreventClose(true);
    setEmailAddresses(allItems);

    // Reset prevent close after a short delay
    setTimeout(() => {
      setPreventClose(false);
    }, 100);
  };

  return (
    <div className="w180p">
      <Popover
        activator={
          <Button
            disclosure
            onClick={() => setPopoverOpen(!popoverOpen)}
            icon={<SVGIcon name="SendWhite" />}
            disabled={disabled}
          >
            <FormattedMessage id="admin.alertPolicy.actions.sendEmail" />
          </Button>
        }
        active={popoverOpen}
        onClose={handlePopoverClose}
        preferredAlignment="right"
        preferredPosition="below"
      >
        <div className="p20" style={{ minWidth: '400px' }}>
          <DisplayText size="large" isRequired>
            <FormattedMessage id="app.mail.recepients.to" />
          </DisplayText>
          <div>
            <InlineMultiTextField
              name="email-addresses"
              values={emailAddresses}
              placeholder={getTranslatedText(
                intl,
                'product.onboarding.msp.invite.employees.input.pl'
              )}
              onItemAdded={handleItemAdded}
              onItemDeleted={handleItemDeleted}
              showAvatar={true}
              showBorder={true}
            />
            {emailValidation.hasInvalidEmails && (
              <div className="mt5">
                <TextStyle variation="negative">
                  <FormattedMessage id="mdm.settings.invalid.email.format" />:{' '}
                  {emailValidation.invalidEmails.join(', ')}
                </TextStyle>
              </div>
            )}
          </div>
          <div className="mt20 flex g10 justify-end">
            <Button plainSecondary onClick={handleCancel}>
              <FormattedMessage id="common.cancel" />
            </Button>
            <Button
              primary
              loading={sendEmailLoading}
              icon={<SVGIcon name="SendWhite" />}
              disabled={!emailValidation.allValid}
              onClick={handleSendEmail}
            >
              <FormattedMessage id="common.send" />
            </Button>
          </div>
        </div>
      </Popover>
    </div>
  );
};

export default EmailSender;
